import {
  emptyLibraryDraft,
  findLikelyDuplicateLibraryItems,
  libraryItemStatusOptions,
  type LibraryItem,
  type LibraryItemStatus,
  type LibrarySearchResult,
} from "@/lib/library";
import type { LibraryItem as PersistedLibraryItem } from "@/lib/library";

export type ImportMode = "auto" | "isbn" | "lines" | "csv";

export type ImportDraft = LibrarySearchResult & {
  notes?: string | null;
  importLabel: string;
  duplicateWarnings: ReturnType<typeof findLikelyDuplicateLibraryItems>;
  validationIssues: string[];
};

export const getBatchDuplicateSignature = (
  draft: Pick<
    ImportDraft,
    | "title"
    | "displayTitle"
    | "authorText"
    | "authors"
    | "isbn10"
    | "isbn13"
    | "sourceProvider"
    | "sourceId"
    | "googleBooksId"
    | "openLibraryId"
  >,
) =>
  [
    draft.displayTitle ?? draft.title,
    draft.authorText ?? draft.authors.join(", "),
    draft.isbn13 ?? "",
    draft.isbn10 ?? "",
    draft.sourceProvider,
    draft.sourceId ?? "",
    draft.googleBooksId ?? "",
    draft.openLibraryId ?? "",
  ]
    .map((value) => value.trim().toLocaleLowerCase())
    .join("|");

export const toComparableItem = (draft: ImportDraft, index: number): LibraryItem => ({
  id: `import-draft-${index}`,
  workspace_id: "import-preview",
  source_provider: draft.sourceProvider,
  source_id: draft.sourceId,
  source_type: "book",
  is_owned: false,
  google_books_id: draft.googleBooksId,
  open_library_id: draft.openLibraryId,
  title: draft.title,
  subtitle: draft.subtitle,
  display_title: draft.displayTitle,
  author_text: draft.authorText,
  authors: draft.authors,
  publisher: draft.publisher,
  published_date: draft.publishedDate,
  published_year: draft.publishedYear,
  language_code: draft.languageCode,
  isbn_10: draft.isbn10,
  isbn_13: draft.isbn13,
  edition: draft.edition,
  volume: draft.volume,
  series: draft.series,
  page_count: draft.pageCount,
  cover_url: draft.coverUrl,
  description: draft.description,
  description_encrypted: null,
  description_preview: null,
  notes: draft.notes ?? null,
  notes_encrypted: null,
  notes_preview: null,
  library_text_key_version: 1,
  metadata: draft.metadata as PersistedLibraryItem["metadata"],
  status: draft.status,
  doi: null,
  url: null,
  accessed_date: null,
  container_title: null,
  body_encrypted: null,
  body_preview: null,
  body_key_version: 1,
  ingested_from: null,
  source_file_path: null,
  source_file_mime: null,
  source_file_size_bytes: null,
  embedding_status: "none",
  embedding_refreshed_at: null,
  chunk_count: 0,
  last_embedding_error: null,
  citation_style_override: null,
  book_binding: null,
  is_first_edition: false,
  is_signed: false,
  is_numbered: false,
  numbered_index: null,
  numbered_total: null,
  purchase_cost_cents: null,
  replacement_cost_cents: null,
  open_access_url: null,
  created_at: "",
  updated_at: "",
});

const splitCsvRow = (line: string) => {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      values.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
};

const getYear = (value: string) => {
  const match = value.match(/(\d{4})/);
  return match ? Number.parseInt(match[1], 10) : null;
};

const normalizeImportedIdentifier = (value: string | null | undefined) =>
  value?.replace(/[^0-9xX]/g, "").toUpperCase() ?? "";

const getCsvIdentifierValidationIssues = ({
  isbn13,
  isbn10,
}: {
  isbn13?: string | null;
  isbn10?: string | null;
}) => {
  const issues: string[] = [];

  if (isbn13 && normalizeImportedIdentifier(isbn13).length !== 13) {
    issues.push("Invalid ISBN-13");
  }

  if (isbn10 && normalizeImportedIdentifier(isbn10).length !== 10) {
    issues.push("Invalid ISBN-10");
  }

  return issues;
};

const createDraft = (partial: Partial<ImportDraft>, importLabel: string): ImportDraft => ({
  ...emptyLibraryDraft(),
  notes: null,
  importLabel,
  duplicateWarnings: [],
  validationIssues: [],
  ...partial,
});

export const getDraftKey = (draft: Pick<ImportDraft, "importLabel">, index: number) =>
  `${draft.importLabel}-${index}`;

export const getDraftTestId = (draft: Pick<ImportDraft, "title" | "importLabel">) =>
  `review-row-${(draft.title || draft.importLabel)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}`;

export const parseImportText = (raw: string, mode: ImportMode) => {
  const lines = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length === 0) return [] as ImportDraft[];

  if (mode === "csv" || (mode === "auto" && /,/.test(lines[0]) && /(title|author|isbn)/i.test(lines[0]))) {
    const header = splitCsvRow(lines[0]).map((value) => value.trim().toLowerCase());
    return lines.slice(1).map((line, index) => {
      const cells = splitCsvRow(line);
      const get = (key: string) => {
        const headerIndex = header.indexOf(key);
        return headerIndex >= 0 ? cells[headerIndex] ?? "" : "";
      };

      const title = get("title") || get("book_title");
      const authorText = get("author") || get("author_text");
      const publishedDate = get("publisheddate") || get("published_date");
      const isbn13 = get("isbn13") || get("isbn_13") || null;
      const isbn10 = get("isbn10") || get("isbn_10") || null;
      const status = libraryItemStatusOptions.some((option) => option.value === get("status"))
        ? (get("status") as LibraryItemStatus)
        : "to_read";

      return createDraft(
        {
          title: title || `Imported row ${index + 1}`,
          authorText: authorText || null,
          authors: authorText ? authorText.split(",").map((value) => value.trim()).filter(Boolean) : [],
          publisher: get("publisher") || null,
          publishedDate: publishedDate || null,
          publishedYear: getYear(publishedDate),
          isbn13,
          isbn10,
          edition: get("edition") || null,
          volume: get("volume") || null,
          series: get("series") || null,
          languageCode: get("language") || get("languagecode") || get("language_code") || null,
          notes: get("notes") || null,
          status,
          validationIssues: getCsvIdentifierValidationIssues({ isbn13, isbn10 }),
        },
        `CSV row ${index + 2}`,
      );
    });
  }

  if (mode === "isbn" || (mode === "auto" && lines.every((line) => /^[0-9xX\s-]+$/.test(line)))) {
    return lines.map((line, index) => {
      const normalized = normalizeImportedIdentifier(line);
      return createDraft(
        {
          title: normalized,
          isbn13: normalized.length === 13 ? normalized : null,
          isbn10: normalized.length === 10 ? normalized : null,
          validationIssues: normalized.length === 10 || normalized.length === 13 ? [] : ["Invalid ISBN"],
        },
        `ISBN ${index + 1}`,
      );
    });
  }

  return lines.map((line, index) => {
    const [titlePart, authorPart] = line.split(/\s+[—–-]\s+|\s*\|\s*/);
    const publishedDate = line.match(/\((\d{4})\)$/)?.[1] ?? "";
    const cleanTitle = titlePart.replace(/\((\d{4})\)$/, "").trim();
    return createDraft(
      {
        title: cleanTitle || `Imported line ${index + 1}`,
        authorText: authorPart?.replace(/\((\d{4})\)$/, "").trim() || null,
        authors: authorPart ? authorPart.split(",").map((value) => value.trim()).filter(Boolean) : [],
        publishedDate: publishedDate || null,
        publishedYear: getYear(publishedDate),
      },
      `Line ${index + 1}`,
    );
  });
};
